import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Check,
  ChevronDown,
  Copy,
  Download,
  File,
  FileText,
  PenLine,
  Save
} from "lucide-react";

import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent
} from "@/components/ai-elements/message";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Tag } from "@/components/ui/tag";
import { AssistantByline } from "@/features/agents/components/AssistantByline";
import { markdownToPlainText } from "../utils/markdownPlainText";

type Props = {
  content: string;
  timestampLabel?: string;
  sourcesUsed?: string[] | null;
  /**
   * "chat" (default) is the standard Q/A bubble. "draft" renders the reply as a
   * white document card with a "Letter Drafting" header — used for turns that
   * had a draft attached so they read like a document, not a chat answer.
   */
  variant?: "chat" | "draft";
  /** Allow copying this archived reply into a draft. */
  canSaveToDraft?: boolean;
  saveToDraftLabel?: string | null;
  onSaveToDraft?: () => void;
  /** Allow downloading this reply as a Word/PDF document. */
  canDownload?: boolean;
  onDownload?: (format: "docx" | "pdf") => void;
};

function MessageBubbleAssistantComponent({
  content,
  timestampLabel,
  sourcesUsed = null,
  variant = "chat",
  canSaveToDraft = false,
  saveToDraftLabel = null,
  onSaveToDraft,
  canDownload = false,
  onDownload
}: Props) {
  const isDraft = variant === "draft";
  const plainTextContent = useMemo(
    () => markdownToPlainText(content),
    [content]
  );
  const [hasCopied, setHasCopied] = useState(false);
  const copyResetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
        copyResetTimeoutRef.current = null;
      }
    };
  }, []);

  const markCopied = () => {
    setHasCopied(true);
    if (copyResetTimeoutRef.current)
      window.clearTimeout(copyResetTimeoutRef.current);
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setHasCopied(false);
      copyResetTimeoutRef.current = null;
    }, 1200);
  };
  const onCopy = () => {
    navigator.clipboard
      .writeText(plainTextContent)
      .then(markCopied)
      .catch(() => {});
  };

  const markdownNode = (
    <div className="w-full min-w-0 text-left text-md wrap-break-word wrap-anywhere text-fg">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children, ...props }) => (
            <p {...props} className="my-1.5 first:mt-0 last:mb-0">
              {children}
            </p>
          ),
          h1: ({ children, ...props }) => (
            <h1 {...props} className="mt-4 mb-2 text-lg font-medium first:mt-0">
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2
              {...props}
              className="mt-4 mb-2 text-md font-semibold first:mt-0"
            >
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3
              {...props}
              className="mt-3.5 mb-1.5 text-md font-semibold first:mt-0"
            >
              {children}
            </h3>
          ),
          a: ({ children, ...props }) => (
            <a
              {...props}
              className="underline wrap-break-word wrap-anywhere"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          ),
          code: ({ children, ...props }) => (
            <code
              {...props}
              className="rounded-xs bg-fill-2 px-1 font-mono text-base wrap-break-word wrap-anywhere"
            >
              {children}
            </code>
          ),
          pre: ({ children, ...props }) => (
            <pre
              {...props}
              className="my-2 w-full overflow-x-auto rounded-lg bg-fill-1 p-3 first:mt-0 last:mb-0 [&_code]:bg-transparent [&_code]:px-0"
            >
              {children}
            </pre>
          ),
          ul: ({ children, ...props }) => (
            <ul
              {...props}
              className="my-1.5 list-disc space-y-1 pl-5 first:mt-0 last:mb-0"
            >
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol
              {...props}
              className="my-1.5 list-decimal space-y-1 pl-5 first:mt-0 last:mb-0"
            >
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li {...props} className="pl-0">
              {children}
            </li>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  const hasContent = Boolean(content.trim());

  return (
    <Message from="assistant">
      <AssistantByline />
      {isDraft ? (
        <div className="flex w-full max-w-190 min-w-0 flex-col overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
          {/* Document-style header so a drafting reply reads like a letter,
              not a chat answer. */}
          <div className="flex h-9 items-center gap-2 border-b border-line-1 bg-chrome px-4">
            <PenLine className="size-3.5 shrink-0 text-fg-3" aria-hidden />
            <span className="text-sm font-medium text-fg">Letter Drafting</span>
            {saveToDraftLabel ? (
              <span className="min-w-0 truncate text-sm text-fg-3">
                · {saveToDraftLabel}
              </span>
            ) : null}
          </div>
          <div className="px-5 py-4">{markdownNode}</div>
        </div>
      ) : (
        <MessageContent className="max-w-180">{markdownNode}</MessageContent>
      )}
      {hasContent || sourcesUsed?.length ? (
        <MessageActions className="w-full max-w-180 min-w-0 flex-wrap">
          {hasContent ? (
            <MessageAction
              tooltip={hasCopied ? "Copied" : "Copy"}
              label="Copy"
              size="icon-xs"
              onClick={onCopy}
            >
              {hasCopied ? <Check /> : <Copy />}
            </MessageAction>
          ) : null}
          {canDownload && onDownload && hasContent ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="xs" title="Download this reply">
                  <Download />
                  Download
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="bottom"
                align="start"
                sideOffset={6}
                className="w-40"
              >
                <DropdownMenuItem onSelect={() => onDownload("docx")}>
                  <FileText />
                  Word (.docx)
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onDownload("pdf")}>
                  <File />
                  PDF (.pdf)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {canSaveToDraft && onSaveToDraft && hasContent ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={onSaveToDraft}
              title="Save this reply to a draft"
            >
              <Save />
              Save to Draft
            </Button>
          ) : null}
          {sourcesUsed?.length
            ? sourcesUsed.map((s) => <Tag key={s}>{s}</Tag>)
            : null}
        </MessageActions>
      ) : null}
      {timestampLabel ? (
        <span className="text-sm text-fg-3">{timestampLabel}</span>
      ) : null}
    </Message>
  );
}

const sameStringArray = (a?: string[] | null, b?: string[] | null): boolean => {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) return false;
  }
  return true;
};

// Rendering a bubble (ReactMarkdown parse) is expensive, so skip re-rendering
// when the meaningful props are unchanged. `onSaveToDraft`/`onDownload` are
// intentionally ignored: their identity changes each parent render but their
// behavior does not. Source arrays are compared by value because they're
// rebuilt from metadata each render.
function areBubblePropsEqual(prev: Props, next: Props): boolean {
  return (
    prev.content === next.content &&
    prev.variant === next.variant &&
    prev.canSaveToDraft === next.canSaveToDraft &&
    prev.saveToDraftLabel === next.saveToDraftLabel &&
    prev.canDownload === next.canDownload &&
    prev.timestampLabel === next.timestampLabel &&
    sameStringArray(prev.sourcesUsed, next.sourcesUsed)
  );
}

export const MessageBubbleAssistant = React.memo(
  MessageBubbleAssistantComponent,
  areBubblePropsEqual
);
